import { Card, Divider, Space, Tag } from 'antd';

// Read-only chat replay for a stored assistant conversation. Shared by the
// full-page detail screen (ShowConversation) and the inline expandable row on
// the list screen so both render conversations identically.

const getValue = (object, paths) => {
  if (!object) return null;
  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index];
    const value = path.split('.').reduce((current, key) => {
      if (current === undefined || current === null) return null;
      return current[key];
    }, object);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const getListingImage = (listing) =>
  getValue(listing, [
    'image',
    'imageUrl',
    'imageURL',
    'mainImage',
    'coverImage',
    'images.0.url',
    'images.0.imageUrl',
    'images.0.imageURL',
    'media.0.url',
  ]);

const getListingTitle = (listing) =>
  getValue(listing, ['title', 'name', 'headline', 'description']) || 'Listing';

const getListingPrice = (listing) =>
  getValue(listing, ['price', 'priceJod', 'displayPrice', 'formattedPrice']);

const getListingLocation = (listing) => {
  const area = getValue(listing, ['area.name', 'area.title', 'area', 'neighborhood']);
  const city = getValue(listing, ['city.name', 'city.title', 'city']);
  return [area, city].filter(Boolean).join(', ');
};

const chipText = (item) => {
  if (typeof item === 'string') return item;
  return getValue(item, ['label', 'title', 'name', 'text', 'value']) || JSON.stringify(item);
};

const renderPayloadText = (payload) => {
  if (!payload) return null;
  return payload.reply || payload.assistantReply || payload.message || null;
};

const ListingCard = ({ listing }) => {
  const image = getListingImage(listing);
  const title = getListingTitle(listing);
  const price = getListingPrice(listing);
  const location = getListingLocation(listing);
  const listingNumber = getValue(listing, ['listingNumber', 'id', 'listingId']);

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        border: '1px solid #e8e8e8',
        borderRadius: 12,
        background: '#fff',
        padding: 10,
        marginTop: 8,
        maxWidth: 520,
      }}
    >
      {image ? (
        <img
          src={image}
          alt="listing"
          style={{
            width: 96,
            height: 76,
            objectFit: 'cover',
            borderRadius: 8,
            background: '#f5f5f5',
          }}
        />
      ) : (
        <div
          style={{
            width: 96,
            height: 76,
            borderRadius: 8,
            background: '#f5f5f5',
            color: '#999',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
          }}
        >
          No image
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#1f1f1f' }}>{title}</div>
        {price && <div style={{ color: '#0b7a3b', fontWeight: 700 }}>{price} JOD</div>}
        {location && <div style={{ color: '#666' }}>{location}</div>}
        {listingNumber && <Tag style={{ marginTop: 6 }}>{listingNumber}</Tag>}
      </div>
    </div>
  );
};

const ArticleCard = ({ article }) => {
  if (!article) return null;
  const title = typeof article === 'string' ? article : getValue(article, ['title', 'headline', 'name']);
  const description = typeof article === 'string' ? null : getValue(article, ['description', 'summary', 'body']);
  const url = typeof article === 'string' ? null : getValue(article, ['url', 'link']);

  return (
    <Card size="small" style={{ maxWidth: 520, marginTop: 8, borderRadius: 12 }}>
      <strong>{title || 'Article'}</strong>
      {description && <p style={{ margin: '6px 0 0' }}>{description}</p>}
      {url && (
        <a href={url} target="_blank" rel="noreferrer">
          Open article
        </a>
      )}
    </Card>
  );
};

const Chips = ({ title, items }) => {
  const values = toArray(items).filter(Boolean);
  if (!values.length) return null;

  return (
    <div style={{ marginTop: 8 }}>
      {title && <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>{title}</div>}
      <Space wrap>
        {values.map((item, index) => (
          <Tag key={`${title}-${index}`} color="blue">
            {chipText(item)}
          </Tag>
        ))}
      </Space>
    </div>
  );
};

const MessageBubble = ({ role, children }) => {
  const isUser = role === 'user';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        margin: '10px 0',
      }}
    >
      <div
        style={{
          maxWidth: '72%',
          borderRadius: 18,
          padding: '10px 14px',
          background: isUser ? '#1e88e5' : '#f3f5f8',
          color: isUser ? '#fff' : '#1f1f1f',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
        dir="auto"
      >
        {children}
      </div>
    </div>
  );
};

const TurnReplay = ({ turn, index }) => {
  const payload = turn.responsePayload || {};
  const listings = Array.isArray(payload.listings) ? payload.listings : [];
  const assistantText = turn.assistantReply || renderPayloadText(payload);

  return (
    <div style={{ marginBottom: 18 }}>
      {turn.userMessage && <MessageBubble role="user">{turn.userMessage}</MessageBubble>}
      <MessageBubble role="assistant">
        {turn.error ? (
          <>
            <Tag color="red">Error</Tag>
            <div style={{ marginTop: 6 }}>
              {(turn.error && turn.error.message) || 'Assistant turn failed'}
            </div>
          </>
        ) : (
          <>
            {assistantText || <span style={{ color: '#999' }}>No assistant reply text</span>}
            {typeof payload.total === 'number' && (
              <div style={{ marginTop: 8 }}>
                <Tag>{payload.total} results</Tag>
              </div>
            )}
            {listings.map((listing, listingIndex) => (
              <ListingCard key={`${index}-listing-${listingIndex}`} listing={listing} />
            ))}
            <ArticleCard article={payload.article} />
            <Chips title="Did you mean" items={payload.candidates} />
            <Chips title="Suggestions" items={payload.suggestions} />
            {payload.ended && <Tag style={{ marginTop: 8 }}>Ended</Tag>}
          </>
        )}
        <div style={{ marginTop: 8, color: turn.error ? '#8c8c8c' : '#6f7782', fontSize: 12 }}>
          Turn {index + 1} · {formatDate(turn.createdAt)}
          {typeof turn.latencyMs === 'number' ? ` · ${turn.latencyMs}ms` : ''}
        </div>
      </MessageBubble>
    </div>
  );
};

const ConversationReplay = ({ conversation, maxHeight = '72vh' }) => {
  if (!conversation) return null;
  const turns = toArray(conversation.turns);

  return (
    <div
      style={{
        maxHeight,
        overflowY: 'auto',
        padding: '8px 14px',
        background: '#eef2f7',
        borderRadius: 12,
      }}
    >
      {turns.length ? (
        turns.map((turn, index) => (
          <TurnReplay key={`${conversation.id}-turn-${index}`} turn={turn} index={index} />
        ))
      ) : (
        <div style={{ color: '#999' }}>No turns stored.</div>
      )}
      <Divider />
      <div style={{ color: '#8c8c8c', fontSize: 12 }}>
        Read-only replay reconstructed from stored responsePayload values.
      </div>
    </div>
  );
};

export default ConversationReplay;
